import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPaymentCards, setDefaultPaymentCard, deletePaymentCard } from '@/api/payment';
import PaymentCard from '@/components/PaymentCard';
import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import AddPaymentCardModal from '@/components/AddPaymentCardModal';
import addIcon from '@/assets/add_black.svg';
import emptyIcon from '@/assets/dondon_grey.svg';

const PaymentPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);

  const { data: cardsData, isLoading, isError } = useQuery({
    queryKey: ['payment-cards'],
    queryFn: getPaymentCards
  });

  const setDefaultMutation = useMutation({
    mutationFn: setDefaultPaymentCard,
    onSuccess: () => {
      queryClient.invalidateQueries(['payment-cards']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deletePaymentCard,
    onSuccess: () => {
      queryClient.invalidateQueries(['payment-cards']);
      setDeleteDialogOpen(false);
      setCardToDelete(null);
    }
  });

  const cards = cardsData?.data || [];

  const handleSetDefault = (card) => {
    setDefaultMutation.mutate(card.id);
  };

  const handleDeleteClick = (card) => {
    setCardToDelete(card);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (cardToDelete) {
      deleteMutation.mutate(cardToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCardToDelete(null);
  };

  return (
    <div className="w-full flex flex-col justify-start items-start bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="self-stretch flex justify-between items-center">
        <h1 className="text-lg font-bold">{t('me.payment.title')}</h1>
      </div>

      {/* Cards List */}
      <div className="self-stretch py-6 flex justify-start items-start gap-4 flex-wrap">
        {isLoading ? (
          <div className="w-full flex justify-center items-center py-10">
            <p>{t('common.loading')}</p>
          </div>
        ) : isError ? (
          <div className="w-full flex justify-center items-center py-10">
            <p>{t('common.error')}</p>
          </div>
        ) : cards.length > 0 ? (
          <div className='w-full grid grid-cols-2 gap-4'>
            {cards.map((card) => (
              <PaymentCard
                key={card.id}
                card={card}
                isDefault={card.isDefault}
                onSetDefault={handleSetDefault}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        ) : (
          <div className="w-full h-[400px] flex flex-col justify-center items-center">
            <img src={emptyIcon} alt="No payment methods" className="w-[120px] h-[80px] mb-4" />
            <span className="text-gray-400">{t('me.payment.noPaymentMethods')}</span>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={t('me.payment.confirmDelete')}
        cancelText={t('common.cancel')}
        confirmText={t('common.delete')}
      />
    </div>
  );
};

export default PaymentPage;